import {calculateErrorMessage} from 'common/helpers'

export function createAudioPlayer() {
  let audioEl: HTMLAudioElement | null = null

  const playBlob = async (
    blob: Blob,
    {onEnded, onError}: {onEnded?: () => void; onError?: (e: any) => void} = {}
  ) => {
    if (!(blob instanceof Blob)) return
    if (audioEl) {
      try {
        audioEl.pause()
      } catch (e) {
        calculateErrorMessage(e)
      }
      audioEl = null
    }
    const url = URL.createObjectURL(blob)
    const audio = new Audio(url)
    audioEl = audio
    audio.onended = () => {
      try {
        URL.revokeObjectURL(url)
      } catch (e) {
        calculateErrorMessage(e)
      }
      audioEl = null
      onEnded?.()
    }
    audio.onerror = (e) => {
      try {
        URL.revokeObjectURL(url)
      } catch (e) {
        calculateErrorMessage(e)
      }
      audioEl = null
      onError?.(e)
    }
    await audio.play().catch((e) => onError?.(e))
    return audio
  }

  const stop = () => {
    if (!audioEl) return
    try {
      audioEl.onended = null
      audioEl.onerror = null
      audioEl.pause()
    } catch (e) {
      calculateErrorMessage(e)
    }
    try {
      const src = audioEl.src || ''
      if (typeof src === 'string' && src.startsWith('blob:')) URL.revokeObjectURL(src)
    } catch (e) {
      calculateErrorMessage(e)
    }
    audioEl = null
  }

  const syncTranscriptToAudio = (
    audio: HTMLAudioElement,
    transcriptId: string,
    fullText: string,
    onUpdate: (
      update: {
        id: string
        text?: string
        progressiveText?: string
        isComplete?: boolean
        totalText?: string
      }[]
    ) => void
  ) => {
    if (!audio || !transcriptId || !fullText) return

    const mapWordsToTimestamps = (text: string, durationSec: number) => {
      const words = text.trim().split(/\s+/)
      const lengths = words.map((w) => w.length)
      const total = lengths.reduce((a, b) => a + b, 0) || 1
      let cum = 0
      return words.map((w, i) => {
        cum += lengths[i]
        return {word: w, time: (cum / total) * durationSec}
      })
    }

    const startSync = () => {
      const duration = audio.duration || 0
      if (!duration || isNaN(duration) || duration <= 0) return

      const wordTimes = mapWordsToTimestamps(fullText, duration)
      let lastIndex = -1
      let rafId: number | null = null

      const tick = () => {
        const t = audio.currentTime
        while (lastIndex + 1 < wordTimes.length && wordTimes[lastIndex + 1].time <= t) lastIndex++
        const progressiveText = wordTimes
          .slice(0, lastIndex + 1)
          .map((w) => w.word)
          .join(' ')
        onUpdate([{id: transcriptId, text: progressiveText, progressiveText}])
        if (audio.ended) {
          onUpdate([
            {
              id: transcriptId,
              text: fullText,
              progressiveText: fullText,
              isComplete: true,
              totalText: fullText,
            },
          ])
          if (rafId) cancelAnimationFrame(rafId)
          return
        }
        rafId = requestAnimationFrame(tick)
      }

      const onEnded = () => {
        onUpdate([
          {
            id: transcriptId,
            text: fullText,
            progressiveText: fullText,
            isComplete: true,
            totalText: fullText,
          },
        ])
      }

      audio.addEventListener('ended', onEnded)
      rafId = requestAnimationFrame(tick)
    }

    if (audio.readyState >= 1) startSync()
    else audio.addEventListener('loadedmetadata', startSync, {once: true})
  }

  const playAudioBlobAndSync = async (
    blob: Blob,
    transcriptId: string | null,
    fullText: string,
    onUpdate: (
      update: {
        id: string
        text?: string
        progressiveText?: string
        isComplete?: boolean
        totalText?: string
      }[]
    ) => void
  ) => {
    const url = URL.createObjectURL(blob)

    try {
      await new Promise<void>((resolve, reject) => {
        const audio = new Audio(url)

        audio.onended = () => {
          resolve()
        }
        audio.onerror = (e) => {
          reject(e)
        }

        const trySync = () => {
          if (transcriptId && fullText) {
            syncTranscriptToAudio(audio, transcriptId, fullText, onUpdate)
          }
        }

        if (audio.readyState >= 1) trySync()
        else audio.addEventListener('loadedmetadata', trySync, {once: true})

        audio.play().catch(reject)
      })
    } finally {
      try {
        URL.revokeObjectURL(url)
      } catch (e) {
        calculateErrorMessage(e)
      }
    }
  }

  return {playBlob, stop, syncTranscriptToAudio, playAudioBlobAndSync}
}
